import React from 'react';
import styled, { css } from 'styled-components';
import MetaTags from 'components/meta-tags';
import { CommonFooterLayout } from 'src/payments/layout/common/footer';
import { CommonLayoutHeader } from 'src/payments/layout/common/header';
import { ChildrenContainer } from 'src/payments/layout/common/children-container';
import { ImageOrientation, useGetImageOrientation } from 'hooks/use-get-image-orientation';
import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import { useObserver } from 'mobx-react-lite';
import { EnrollmentSteps } from 'src/payments/constants';

const { alreadySignedUp, success, connectBank } = EnrollmentSteps;

type DutchiePayLayoutProps = {
  children: React.ReactNode;
};

export function SignupLayout(props: DutchiePayLayoutProps): JSX.Element | null {
  const { children } = props;
  const DutchiePayEnrollment = useDutchiePayEnrollment();
  const dispensaryLogo = useObserver(() => DutchiePayEnrollment.dispensary.logo ?? '');
  const dispensaryName = useObserver(() => DutchiePayEnrollment.dispensary.name ?? `Company`);
  const currentStep = useObserver(() => DutchiePayEnrollment.currentStep);
  const isCobrandedSignup = useObserver(() => DutchiePayEnrollment.isCobrandedSignup);
  const plaidModal = currentStep === connectBank;
  const currentStepEqualsSuccess = currentStep === success || currentStep === alreadySignedUp;
  const { imageOrientation } = useGetImageOrientation(dispensaryLogo);

  return (
    <>
      <MetaTags />
      <Container cardView={!currentStepEqualsSuccess && !plaidModal}>
        <CommonLayoutHeader />
        <ContentContainer>
          {dispensaryLogo && isCobrandedSignup && (
            <LogoContainer>
              <DispensaryLogo
                src={dispensaryLogo}
                orientation={imageOrientation}
                alt={`${String(dispensaryName)} Logo`}
              />
            </LogoContainer>
          )}
          <ChildrenContainer cardView={!currentStepEqualsSuccess && !plaidModal}>{children}</ChildrenContainer>
          {!currentStepEqualsSuccess && <CommonFooterLayout />}
        </ContentContainer>
      </Container>
    </>
  );
}

const Container = styled.div`
  /* Setting css vars on a parent scope makes them available to all child dom nodes */
  --dp-content-width: 375px;
  height: ${(props) => (props.cardView ? `100%` : '100vh')};
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 24px;
`;

const ContentContainer = styled.div`
  max-width: var(--dp-content-width);
  width: 100%;
  padding: 8px;
`;

export const Section = styled.section<{ cardView?: boolean }>`
  ${({ theme: { colors }, cardView = true }) => css`
    border-radius: 10px;
    width: 100%;
    padding: ${cardView ? `16px` : '0'};
    transform: translateZ(0px);
    overflow: hidden;
    height: ${cardView ? `auto` : '100%'};
    ${cardView &&
    css`
      border: 1px solid ${colors.grey[90]};
    `}
  `}
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
`;

const DispensaryLogo = styled.img`
  max-height: ${(props) => (props.orientation === ImageOrientation.vertical ? '32px' : '56px')};
`;
